/**
 * @packageDocumentation
 * @module f1/asynchronous
 */

import is_function from '../is/is_function';

export interface Task {
  /**
   * Invoke one task with a value
   *
   * @public
   * @function f1.task
   * @param value
   * @example
   *
   * ```ts
   * f1.task(() => fetch('1.json'), f1.skip);
   * f1.task({ invoke() {
   *   return fetch('1.json');
   * }, f1.skip);
   *
   * // or pass a value
   * f1.task((name) => fetch(name), '1.json');
   * ```
   *
   */
  <E, R>(task: TTask<E, R>, value: unknown): Promise<unknown>;
}

export type TTaskFunction<E, R> = (value: E) => Promise<R>;
export type TTaskObject<E, R> = { invoke(value: E): Promise<R> };
export type TTask<E, R> = TTaskFunction<E, R> | TTaskObject<E, R>;

/**
 * @see Task
 */
export default <E, R>(task: TTask<E, R>, value: E): Promise<R> => {
  return is_function(task) ? task(value) : (task as TTaskObject<E, R>).invoke(value);
};
